<script setup lang="ts">
import { computed, onMounted, ref, shallowRef, useTemplateRef, watch, type Ref } from "vue";

import DatasourceToggle from "@/components/datascience/DataSourceToggle.vue";
import ReportMetrics from "@/components/datascience/ReportMetrics.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import ImageWidget from "@/components/ui/ImageWidget.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import { MediaType, type EstimatorReportDto, type ItemRepresentationDto } from "@/dto";
import { useClipboard } from "@/services/clipboard";
import AddToPresentationMenu from "@/views/AddToPresentationMenu.vue";

const { report } = defineProps<{ report: EstimatorReportDto }>();

const clipboard = useClipboard();

const plotSelectedName = ref("");
const plotNames = ref<string[]>([]);
const isTestDatasource = ref(true);
const selectedPlot = shallowRef<ItemRepresentationDto>();
const selectedPlotElement = useTemplateRef<HTMLElement>("selectedPlotElement");
const metricsElement = useTemplateRef<HTMLElement>("metricsElement");

const source = computed(() => (isTestDatasource.value ? "test" : "train"));
const visibleMetrics = computed(() => {
  return report.metrics
    .filter((m) => m.data_source === source.value || m.data_source === null)
    .sort((a, b) => (a.position ?? Number.MAX_VALUE) - (b.position ?? Number.MAX_VALUE));
});
const plotNameForPresentation = computed(() => {
  return `${plotSelectedName.value} (${source.value})`;
});

function setSelectedPlot(name: string) {
  const plots = report.related_items.filter(
    (r) => r.verbose_name === name && r.representation.media_type === "image/svg+xml;base64"
  );
  const plot = plots.find((r) => r.attributes.data_source === source.value);
  if (plot !== undefined) {
    selectedPlot.value = plot.representation;
    plotSelectedName.value = name;
  }
}

function updateRefs() {
  // plots
  const plots = report.related_items.filter(
    (r) => r.category === "performance" && r.representation.media_type === "image/svg+xml;base64"
  );
  plotNames.value = [...new Set(plots.map((r) => r.verbose_name))].sort();
  plotSelectedName.value = plotNames.value[0];
  setSelectedPlot(plotSelectedName.value);
}

async function copySelectedPlot() {
  await copyElementAsPng(selectedPlotElement);
}

async function copyMetrics() {
  await copyElementAsPng(metricsElement);
}

async function copyElementAsPng(elementRef: Ref) {
  if (elementRef.value) {
    await clipboard.copyElementAsPng(elementRef.value);
  }
}

onMounted(() => {
  updateRefs();
});

watch([() => report, isTestDatasource], () => {
  updateRefs();
});
</script>

<template>
  <div class="performance">
    <div class="heading">
      <div class="help">
        Check how well your model performs at a glance. View key metrics, compare them against your
        goals, and interact with live plots to see where your predictions shine, or need
        improvement. This is your quick snapshot of success.
      </div>
      <DatasourceToggle v-model:is-test-datasource="isTestDatasource" />
    </div>
    <div class="plots-and-metrics">
      <div class="plots">
        <div class="block-title label-s">
          <div>
            <i class="title-icon icon icon-bar-chart-2" />
            <DropdownButton
              :label="plotSelectedName"
              icon-right="icon-chevron-down"
              kind="tertiary"
            >
              <DropdownButtonItem
                v-for="name in plotNames"
                :key="name"
                :label="name"
                @click="setSelectedPlot(name)"
              />
            </DropdownButton>
          </div>
          <div class="actions">
            <AddToPresentationMenu
              v-if="selectedPlot !== undefined"
              :name="plotNameForPresentation"
              :report_id="report.id"
              :media_type="selectedPlot.media_type"
              :value="selectedPlot.value"
            />
            <SimpleButton icon-left="icon-copy" kind="tertiary" @click="copySelectedPlot" />
          </div>
        </div>
        <div ref="selectedPlotElement">
          <ImageWidget
            v-if="selectedPlot"
            :mediaType="selectedPlot.media_type"
            :base64-src="selectedPlot.value"
          />
        </div>
      </div>
      <div class="metrics">
        <div class="block-title label-s">
          <div><i class="title-icon icon icon-presentation" /> General metrics</div>
          <div class="actions">
            <AddToPresentationMenu
              :name="`Generel metrics (${source})`"
              :report_id="report.id"
              :media_type="MediaType.Metrics"
              :value="visibleMetrics"
            />
            <SimpleButton icon-left="icon-copy" kind="tertiary" @click="copyMetrics" />
          </div>
        </div>
        <div class="metrics-grid" ref="metricsElement">
          <ReportMetrics :metrics="visibleMetrics" />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.performance {
  --nb-columns: 1;

  display: flex;
  min-height: 100%;
  flex-direction: column;
  margin: var(--spacing-24) 0;

  .heading {
    display: flex;
    flex-direction: row;
    align-items: flex-start;

    .help {
      flex: 2 1 0;
      margin-bottom: var(--spacing-12);
    }
  }

  .plots-and-metrics {
    display: grid;
    gap: var(--spacing-16);
    grid-template-columns: repeat(var(--nb-columns), 1fr);

    .metrics-grid {
      :deep(.metrics) {
        overflow: hidden;
        border: solid var(--color-stroke-background-primary) var(--stroke-width-m);
        border-radius: var(--radius-s);
      }
    }
  }
}

@media screen and (width >= 1025px) {
  .performance {
    --nb-columns: 2;
  }
}
</style>
